const now = () => performance.now();
export const DURATION = 7000;
const STREAMS = 3;

export async function measurePing(t, samples = 12) {
  const times = [];
  for (let i = 0; i < samples; i++) {
    const t0 = now();
    await fetch(t.ping(), { cache: "no-store" });
    times.push(now() - t0);
  }
  times.shift();
  const sorted = [...times].sort((a, b) => a - b);
  const ping = sorted[Math.floor(sorted.length / 2)];
  let jit = 0;
  for (let i = 1; i < times.length; i++) jit += Math.abs(times[i] - times[i - 1]);
  return { ping, jitter: jit / (times.length - 1) };
}

const isAbort = (e) => e?.name === "AbortError";

const meter = (onProgress) => {
  const start = now();
  let bytes = 0;
  const tick = setInterval(() => {
    const secs = (now() - start) / 1000;
    if (secs > 0.2) onProgress((bytes * 8) / secs / 1e6);
  }, 120);
  return {
    add: (n) => { bytes += n; },
    stop: () => {
      clearInterval(tick);
      const secs = (now() - start) / 1000;
      if (!bytes) throw new Error("No data transferred");
      return { mbps: (bytes * 8) / secs / 1e6, bytes };
    },
  };
};

export async function measureDownload(t, onProgress) {
  const m = meter(onProgress);
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), DURATION);
  const stream = async () => {
    try {
      while (!ctl.signal.aborted) {
        const res = await fetch(t.down(), { cache: "no-store", signal: ctl.signal });
        const reader = res.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          m.add(value.length);
        }
      }
    } catch (e) { if (!isAbort(e)) throw e; }
  };
  try { await Promise.all(Array.from({ length: STREAMS }, stream)); }
  finally { clearTimeout(timer); ctl.abort(); }
  return m.stop();
}

const xhrPost = (url, body, signal, onBytes) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  let last = 0;
  xhr.open("POST", url);
  xhr.upload.onprogress = (e) => { onBytes(e.loaded - last); last = e.loaded; };
  xhr.onload = () => { onBytes(body.size - last); resolve(); };
  xhr.onerror = () => reject(new Error("Upload failed"));
  xhr.onabort = () => resolve();
  signal.addEventListener("abort", () => xhr.abort(), { once: true });
  xhr.send(body);
});

export async function measureUpload(t, onProgress) {
  const m = meter(onProgress);
  const chunk = new Blob([crypto.getRandomValues(new Uint8Array(65536))]);
  const payload = new Blob(Array(16).fill(chunk));
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), DURATION);
  const stream = async () => {
    while (!ctl.signal.aborted) await xhrPost(t.up(), payload, ctl.signal, m.add);
  };
  try { await Promise.all(Array.from({ length: STREAMS }, stream)); }
  finally { clearTimeout(timer); ctl.abort(); }
  return m.stop();
}

export function deviceInfo() {
  const c = navigator.connection || {};
  return {
    connection_type: c.type || null,
    effective_type: c.effectiveType || null,
    browser_downlink: c.downlink ?? null,
    browser_rtt: c.rtt ?? null,
    user_agent: navigator.userAgent,
    platform: navigator.userAgentData?.platform || navigator.platform || null,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
